import React from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Code,
  ComponentDefaultProps,
  Flex,
  Text,
} from '@chakra-ui/react';
import { TemplateNamespaceMapping } from 'src/api/v1-api';

interface DynamicFieldsGuideProps extends ComponentDefaultProps {
  namespaceMapping: TemplateNamespaceMapping | undefined;
}

const sections = ['marketplace', 'category', 'listing', 'others'];

const DynamicFieldsGuide = ({ namespaceMapping, ...rest }: DynamicFieldsGuideProps) => {
  const variables: { [key: string]: string[] } = {};
  namespaceMapping?.namespace_mapping?.forEach((field) => {
    const section = field.split('.')[0];
    if (sections.includes(section)) {
      if (!variables[section]) {
        variables[section] = [];
      }
      variables[section].push(field);
    } else {
      if (!variables['others']) {
        variables['others'] = [];
      }
      variables['others'].push(field);
    }
  });

  return (
    <Flex direction="column" wordBreak={'break-word'} {...rest}>
      <Text mt={4} fontSize="md" color="gray.500" fontWeight="bold">
        Dynamic fields
      </Text>
      <Text my={4} fontSize="sm">
        Find below the available dynamic fields that you can use in the template.
        <br />
        You can use these fields in the template by wrapping them in double curly braces. For
        example, if you want to use the title of the listing, you can use{' '}
        <Code>{'Check this {{listing.title}}'}</Code>
      </Text>
      {namespaceMapping?.namespace_mapping && (
        <Accordion allowMultiple>
          {sections.map(
            (section) =>
              variables[section] && (
                <AccordionItem key={section}>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" textTransform="capitalize">
                      {section}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <ul style={{ marginLeft: '24px' }}>
                      {variables[section].map((field) => (
                        <li key={field}>
                          {field}
                          {namespaceMapping.fields_description &&
                            namespaceMapping.fields_description[field] && (
                              <ul style={{ marginLeft: '16px' }}>
                                <li>Type: {namespaceMapping.fields_description[field].type}</li>
                                {namespaceMapping.fields_description[field].type === 'method' &&
                                  namespaceMapping.fields_description[field].arguments && (
                                    <li>
                                      Arguments:
                                      <ul style={{ marginLeft: '16px' }}>
                                        {(
                                          namespaceMapping.fields_description[field].arguments ?? []
                                        ).map((arg) => (
                                          <li key={arg}>{arg}</li>
                                        ))}
                                      </ul>
                                    </li>
                                  )}
                                {namespaceMapping.fields_description[field].examples && (
                                  <li>
                                    Examples:
                                    <ul style={{ marginLeft: '16px' }}>
                                      {namespaceMapping.fields_description[field].examples.map(
                                        (example) => (
                                          <li key={example}>
                                            <Code>{example}</Code>
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </li>
                                )}
                              </ul>
                            )}
                        </li>
                      ))}
                    </ul>
                  </AccordionPanel>
                </AccordionItem>
              ),
          )}
        </Accordion>
      )}
    </Flex>
  );
};

export default DynamicFieldsGuide;
